import React, { useState, useEffect, useMemo, useCallback } from "react";
import PageHeader from "../components/common/PageHeader";
import useStore from "../store/useStore";
import api from "../services/api";
import { Assessment, Print } from "@mui/icons-material";
import { resolveAssetUrl } from "../utils/assetUrl";
import reportService from "../services/reportService";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

const Report = () => {
  const { toast, user } = useStore();
  const [reportType, setReportType] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState("");
  const [companyInfo, setCompanyInfo] = useState(null);

  // Daily Report
  const [dailyDate, setDailyDate] = useState("");

  // Monthly Report
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const fetchOwners = useCallback(async () => {
    try {
      const data = await reportService.getOwners();
      setOwners(data);
    } catch (error) {
      console.error("Failed to load report owners:", error);
      toast(error.response?.data?.message || "Failed to load owners", "error");
    }
  }, [toast]);

  // Pull the active company branding once so generated reports match the logged-in company.
  const fetchCompanyInfo = useCallback(async () => {
    if (user?.role === "SuperAdmin") {
      setCompanyInfo(null);
      return;
    }

    try {
      const res = await api.get("/companies/my-company");
      setCompanyInfo(res.data?.data || null);
    } catch (error) {
      console.error("Failed to load company information for report header:", error);
      setCompanyInfo(null);
      toast(error.response?.data?.message || "Failed to load company info", "error");
    }
  }, [toast, user?.role]);

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  useEffect(() => {
    fetchCompanyInfo();
  }, [fetchCompanyInfo]);

  const reportCompany = useMemo(() => {
    if (user?.role === "SuperAdmin") {
      return {
        companyName: "GAMRENT SuperAdmin",
        address: "Platform-wide reports",
        phone: user?.phone || "",
        email: user?.email || "",
        logo: null
      };
    }

    return companyInfo || {
      companyName: user?.companyName || "Company Name",
      address: user?.companyAddress || "Address",
      phone: user?.companyPhone || "Phone",
      email: user?.email || "Email",
      logo: user?.companyLogo || null
    };
  }, [companyInfo, user]);

  const companyLogoUrl = useMemo(
    () => resolveAssetUrl(reportCompany?.logo),
    [reportCompany?.logo]
  );

  const handleGenerateReport = async () => {
    setReportError("");

    if (!reportType) {
      toast("Please select a report type", "error");
      return;
    }
    if (reportType === "Daily Report" && !dailyDate) {
      toast("Please select a date for the daily report", "error");
      return;
    }
    if ((reportType === "Monthly Report" || reportType === "Owner Report") && (!selectedMonth || !selectedYear)) {
      toast("Please select a month and year", "error");
      return;
    }
    if (reportType === "Owner Report" && !ownerId) {
      toast("Please select an owner", "error");
      return;
    }

    try {
      setLoading(true);
      let data = null;

      // Route each report type to its backend endpoint.
      if (reportType === "Daily Report") {
        data = await reportService.generateDailyReport(dailyDate);
      }

      if (reportType === "Monthly Report") {
        data = await reportService.generateMonthlyReport(selectedMonth, selectedYear);
      }

      if (reportType === "Asset Report") {
        data = await reportService.generateAssetReport();
      }

      if (reportType === "Debtors Report") {
        data = await reportService.generateDebtorsReport();
      }

      if (reportType === "Owner Report") {
        data = await reportService.generateOwnerReport(ownerId, selectedMonth, selectedYear);
        const selectedOwner = owners.find((item) => String(item.id) === String(ownerId));
        data = {
          ...data,
          ownerName: selectedOwner?.name || data?.ownerName || "Unknown Owner",
          month: selectedMonth,
          year: selectedYear
        };
      }

      setReportData({
        ...data,
        generatedAt: new Date().toISOString()
      });
      toast("Report generated successfully!", "success");
    } catch (error) {
      console.error("Failed to generate report:", error);
      const baseMessage = error.response?.data?.message || "Failed to generate report";
      const backendDetail = error.response?.data?.error;
      const message = backendDetail ? `${baseMessage}: ${backendDetail}` : baseMessage;
      setReportError(message);

      // Keep the preview visible so users get immediate feedback even when API fails.
      setReportData({
        type: reportType,
        generatedAt: new Date().toISOString(),
        dailyDate: reportType === "Daily Report" ? dailyDate : null,
        month: reportType === "Monthly Report" || reportType === "Owner Report" ? selectedMonth : null,
        year: reportType === "Monthly Report" || reportType === "Owner Report" ? selectedYear : null,
        ownerName: reportType === "Owner Report" ? (owners.find((item) => String(item.id) === String(ownerId))?.name || "") : null,
        isFallback: true
      });

      toast(baseMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!reportData) return;

    const reportTitle = reportData.type || "Report";
    const printDateTime = new Date(reportData.generatedAt || new Date()).toLocaleString("en-GB");
    const printDate = new Date(reportData.generatedAt || new Date()).toLocaleDateString("en-GB");
    const reportDate = reportData.dailyDate ? new Date(reportData.dailyDate).toLocaleDateString("en-GB", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "";
    const dailyDisplayDate = reportData.dailyDate ? new Date(reportData.dailyDate).toLocaleDateString("en-GB", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "";
    const ownerName = reportData.ownerName || reportData.owner || "";
    const reportWindow = window.open("", "_blank", "width=1100,height=800");

    if (!reportWindow) {
      toast("Please allow pop-ups to print the report", "error");
      return;
    }

    const incomeRows = (reportData.paymentItems || []).map((item) => `
      <tr>
        <td>${item.tenantName || "-"}</td>
        <td>${item.assetName || "-"}</td>
        <td>${item.location || "-"}</td>
        <td>${item.paymentDate || item.paidFor || "-"}</td>
        <td>${item.contractPeriod || item.billingPeriod || "-"}</td>
        <td>${item.duration || item.billingPeriod || "-"}</td>
        <td class="amount">${formatMoney(item.amount, false, item.currency)}</td>
      </tr>
    `).join("");

    const expenseRows = (reportData.maintenanceItems || []).map((item) => `
      <tr>
        <td>${item.assetName || "-"}</td>
        <td>${item.location || "-"}</td>
        <td>${item.maintenanceType || "-"}</td>
        <td>${item.description || "-"}</td>
        <td>${item.date || "-"}</td>
        <td class="amount">${formatMoney(item.cost)}</td>
      </tr>
    `).join("");

    const debtorsRows = (reportData.items || []).map((item) => `
      <tr>
        <td>${item.tenantName || "-"}</td>
        <td>${item.contactNumber || "-"}</td>
        <td>${item.assetName || "-"}</td>
        <td>${item.unitNumber || "-"}</td>
        <td>${item.contractPeriod || [item.contractStart, item.contractEnd].filter(Boolean).join(" - ") || "-"}</td>
        <td class="amount">${formatMoney(item.totalPaid, true)}</td>
        <td class="amount">${formatMoney(item.pendingBalance, true)}</td>
        <td>${item.paymentMethod || "Cash"}</td>
        <td>${item.paymentStatus || "Pending"}</td>
      </tr>
    `).join("");

    const generalRows = (reportData.items || []).map((item) => `
      <tr>
        <td>${item.assetName || "-"}</td>
        <td>${item.unitNumber || "-"}</td>
        <td>${item.tenantName || "-"}</td>
        <td class="amount">${formatMoney(item.collected ?? item.totalPaid)}</td>
        <td class="amount">${item.pendingBalance !== undefined ? formatMoney(item.pendingBalance, true) : "-"}</td>
      </tr>
    `).join("");

    const summaryBlock = (() => {
      if (reportData.type === "Daily Report") {
        return `
          <table class="summary-table">
            <tr><td>Total Income:</td><td class="amount income">${formatMoney(reportData.totalAmount)}</td></tr>
            <tr><td>Total Expenses:</td><td class="amount expense">${formatMoney(reportData.totalExpenses)}</td></tr>
            <tr><td>Net Balance:</td><td class="amount balance">${formatMoney(reportData.netBalance)}</td></tr>
          </table>
        `;
      }

      if (reportData.type === "Monthly Report") {
        return `
          <div class="stats-grid">
            <div class="stat">Total Transactions<span>${reportData.totalTransactions ?? 0}</span></div>
            <div class="stat">Total Income<span>${formatMoney(reportData.totalAmount)}</span></div>
            <div class="stat">Period<span>${selectedMonthLabel} ${selectedYear}</span></div>
          </div>
        `;
      }

      if (reportData.type === "Asset Report") {
        return `
          <div class="stats-grid">
            <div class="stat">Total Assets<span>${reportData.totalAssets ?? 0}</span></div>
            <div class="stat">Residential Assets<span>${reportData.residentialAssets ?? 0}</span></div>
            <div class="stat">Commercial Assets<span>${reportData.commercialAssets ?? 0}</span></div>
          </div>
        `;
      }

      if (reportData.type === "Debtors Report") {
        return `
          <div class="stats-grid">
            <div class="stat">Debtors<span>${(reportData.items || []).length}</span></div>
            <div class="stat">Total Pending<span>${formatMoney((reportData.items || []).reduce((sum, item) => sum + Number(item.pendingBalance || 0), 0), true)}</span></div>
            <div class="stat">Total Paid<span>${formatMoney((reportData.items || []).reduce((sum, item) => sum + Number(item.totalPaid || 0), 0), true)}</span></div>
          </div>
        `;
      }

      if (reportData.type === "Owner Report") {
        return `
          <div class="stats-grid">
            <div class="stat">Total Revenue<span>${formatMoney(reportData.totalRevenue ?? reportData.totalCollected)}</span></div>
            <div class="stat">Management Commission<span>${formatMoney(reportData.managementCommission)}</span></div>
            <div class="stat">Net Amount To Owner<span>${formatMoney(reportData.netAmountToOwner)}</span></div>
          </div>
        `;
      }

      return `
        <div class="stats-grid">
          <div class="stat">Total Transactions<span>${reportData.totalTransactions ?? 0}</span></div>
          <div class="stat">Total Amount<span>${formatMoney(reportData.totalAmount)}</span></div>
          <div class="stat">Occupied Units<span>${reportData.occupiedUnits ?? 0}</span></div>
        </div>
      `;
    })();
    
    const monthlyRows = (reportData.items || []).map((item) => `
      <tr>
        <td>${item.paymentDate || "-"}</td>
        <td>${item.assetName || "-"}${item.location ? ` - ${item.location}` : ""}${item.billingPeriod ? ` (${item.billingPeriod})` : ""}</td>
        <td>${item.tenantName || "-"}</td>
        <td class="amount">${formatMoney(item.amount, false, item.currency)}</td>
      </tr>
    `).join("");

    const assetRows = (reportData.items || []).map((item) => `
      <tr>
        <td>${item.assetName || "-"}</td>
        <td>${item.category || "-"}</td>
        <td>${item.location || "-"}</td>
        <td>${item.description || "-"}</td>
        <td class="amount">${item.rentalSpace ?? 0}</td>
        <td>${item.ownerName || "-"}</td>
      </tr>
    `).join("");

    const bodyHtml = (() => {
      if (reportData.type === "Daily Report") {
        return `
          <h3 class="report-heading">Daily Transactions Report - ${dailyDisplayDate || reportDate || printDate}</h3>
          <h3 class="section-title">INCOME - RENT PAYMENTS</h3>
          <table>
            <thead>
              <tr>
                <th>Tenant Name</th><th>Asset Name</th><th>Location</th><th>Payment Date</th><th>Contract Period</th><th>Duration</th><th>Amount Paid</th>
              </tr>
            </thead>
            <tbody>${incomeRows || '<tr><td colspan="7" class="empty">No income entries</td></tr>'}</tbody>
            <tfoot><tr><td colspan="6" class="right">Total Income:</td><td class="amount">${formatMoney(reportData.totalAmount)}</td></tr></tfoot>
          </table>

          <h3 class="section-title">EXPENSES - MAINTENANCE</h3>
          <table>
            <thead>
              <tr>
                <th>Asset Name</th><th>Location</th><th>Maintenance Type</th><th>Description</th><th>Date</th><th>Cost (D)</th>
              </tr>
            </thead>
            <tbody>${expenseRows || '<tr><td colspan="6" class="empty">No expenses entries</td></tr>'}</tbody>
            <tfoot><tr><td colspan="5" class="right">Total Expenses:</td><td class="amount">${formatMoney(reportData.totalExpenses)}</td></tr></tfoot>
          </table>

          <h3 class="summary-heading">DAILY SUMMARY</h3>
          ${summaryBlock}
        `;
      }
      
      if (reportData.type === "Monthly Report") {
        const monthlyLabel = `${selectedMonthLabel} ${selectedYear}`;
        return `
          <h3 class="report-heading">Monthly Transactions Report - ${monthlyLabel}</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th><th>Description</th><th>Tenant</th><th>Amount (D)</th>
              </tr>
            </thead>
            <tbody>${monthlyRows || '<tr><td colspan="4" class="empty">No monthly transactions</td></tr>'}</tbody>
            <tfoot><tr><td colspan="3" class="right">Total Income:</td><td class="amount">${formatMoney(reportData.totalAmount)}</td></tr></tfoot>
          </table>
          <h3 class="summary-heading">MONTHLY SUMMARY</h3>
          ${summaryBlock}
        `;
      }

      if (reportData.type === "Asset Report") {
        return `
          <h3 class="report-heading">Asset Report</h3>
          <div class="report-date">Date: ${printDate}</div>
          <table>
            <thead>
              <tr><th>Asset Name</th><th>Category</th><th>Location</th><th>Description</th><th>Number of Units</th><th>Owner Name</th></tr>
            </thead>
            <tbody>${assetRows || '<tr><td colspan="6" class="empty">No assets found</td></tr>'}</tbody>
          </table>
        `;
      }

      if (reportData.type === "Debtors Report") {
        return `
          <h3 class="report-heading">Debtors Report</h3>
          <div class="report-date">Date: ${printDate}</div>
          <table>
            <thead>
              <tr><th>Tenant Name</th><th>Contact Number</th><th>Asset</th><th>Unit Number</th><th>Contract Period</th><th>Amount Paid</th><th>Pending Balance</th><th>Payment Method</th><th>Status</th></tr>
            </thead>
            <tbody>${debtorsRows || '<tr><td colspan="9" class="empty">No debtors found</td></tr>'}</tbody>
          </table>
        `;
      }

      if (reportData.type === "Owner Report") {
        const ownerPeriod = `${MONTHS[(Number(reportData.month) || selectedMonth) - 1] || ""} ${reportData.year || selectedYear}`.trim();
        return `
          <h3 class="report-heading">Owner Report - ${ownerName || '-'}</h3>
          <div class="report-date">${ownerPeriod || printDate}</div>
          <h3 class="section-title">INCOME - RENT PAYMENTS</h3>
          <h3 class="section-title">EXPENSES - MAINTENANCE & REPAIRS</h3>
          <table>
            <thead>
              <tr><th>Tenant Name</th><th>Asset Name</th><th>Location</th><th>Payment Date</th><th>Contract Period</th><th>Duration</th><th>Amount Paid</th></tr>
            </thead>
            <tbody>${(reportData.paymentItems || []).length > 0 ? incomeRows : '<tr><td colspan="7" class="empty">No payment data found for the selected owner and month.</td></tr>'}</tbody>
            <tfoot><tr><td colspan="6" class="right">Total Income:</td><td class="amount">${formatMoney(reportData.totalRevenue ?? reportData.totalCollected)}</td></tr></tfoot>
          </table>

          <table>
            <thead>
              <tr><th>Asset Name</th><th>Location</th><th>Maintenance Type</th><th>Description</th><th>Date</th><th>Cost (D)</th></tr>
            </thead>
            <tbody>${(reportData.maintenanceItems || []).length > 0 ? expenseRows : '<tr><td colspan="6" class="empty">No maintenance expenses found for the selected owner and month.</td></tr>'}</tbody>
            <tfoot><tr><td colspan="5" class="right">Total Expenses:</td><td class="amount">${formatMoney(reportData.totalMaintenanceExpenses)}</td></tr></tfoot>
          </table>

          <h3 class="summary-heading">FINANCIAL SUMMARY</h3>
          <table class="summary-table">
            <tr><td>Total Revenue:</td><td class="amount">${formatMoney(reportData.totalRevenue ?? reportData.totalCollected)}</td></tr>
            <tr><td>Management Commission (10%):</td><td class="amount">${formatMoney(reportData.managementCommission)}</td></tr>
            <tr><td>Maintenance Expenses:</td><td class="amount">${formatMoney(reportData.totalMaintenanceExpenses)}</td></tr>
            <tr><td>Net Amount To Owner:</td><td class="amount">${formatMoney(reportData.netAmountToOwner)}</td></tr>
          </table>
          <div class="verified">Authorized by: ____________________&nbsp;&nbsp;&nbsp;&nbsp;Received by: ____________________</div>
        `;
      }

      return `
        <h3 class="report-heading">${reportTitle}</h3>
        ${summaryBlock}
        ${Array.isArray(reportData.items) && reportData.items.length > 0 ? `
          <table>
            <thead>
              <tr><th>Asset</th><th>Unit</th><th>Tenant</th><th>Collected</th><th>Pending</th></tr>
            </thead>
            <tbody>${generalRows}</tbody>
          </table>
        ` : ''}
      `;
    })();

    reportWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${reportTitle}</title>
        <style>
          @page { size: A4; margin: 12mm; }
          * { box-sizing: border-box; }
          body { margin: 0; font-family: Georgia, 'Times New Roman', serif; color: #111; background: #fff; }
          .page { max-width: 760px; margin: 0 auto; padding: 8px 6px 20px; }
          .company { text-align: center; margin-bottom: 18px; }
          .logo { width: 56px; height: 56px; object-fit: contain; display: block; margin: 0 auto 6px; }
          .company-name { font-size: 19px; font-weight: 700; text-transform: uppercase; line-height: 1.15; }
          .company-address, .company-contact { font-size: 11px; margin-top: 2px; }
          .report-title { text-align: center; font-size: 17px; font-weight: 700; margin: 16px 0 2px; }
          .report-subtitle { text-align: center; font-size: 12px; margin-bottom: 8px; }
          .report-date { text-align: right; font-size: 11px; margin: 0 0 14px; }
          .report-heading { text-align: center; font-size: 14px; font-weight: 700; margin: 8px 0 12px; }
          .meta-line { font-size: 12px; margin: 4px 0; }
          .section-title { font-size: 13px; font-weight: 700; margin: 18px 0 8px; text-transform: uppercase; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 11px; }
          th, td { border: 1px solid #222; padding: 6px 7px; vertical-align: top; }
          thead th { background: #ececec; }
          tfoot td { font-weight: 700; background: #f8f8f8; }
          .amount, .right { text-align: right; }
          .empty { text-align: center; padding: 12px; }
          .summary-heading { text-align: center; font-size: 14px; font-weight: 700; margin: 16px 0 10px; text-transform: uppercase; }
          .summary-table { width: 52%; margin: 0 auto 16px; }
          .summary-table td { background: #fff; }
          .summary-table tr td:first-child { width: 65%; font-weight: 700; }
          .income { background: #e7f7e8; }
          .expense { background: #fbe2df; }
          .balance { background: #ddf1d8; }
          .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 8px 0 16px; }
          .stat { border: 1px solid #222; padding: 8px 10px; background: #f8f8f8; font-size: 11px; font-weight: 700; }
          .stat span { display: block; margin-top: 4px; font-size: 13px; }
          .footer-note { margin-top: 24px; font-size: 11px; }
          .verified { margin-top: 28px; font-size: 11px; }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="company">
            ${companyLogoUrl ? `<img src="${companyLogoUrl}" class="logo" alt="Company logo" />` : ''}
            <div class="company-name">${reportCompany?.companyName || 'Company Name'}</div>
            <div class="company-address">${reportCompany?.address || ''}</div>
            <div class="company-contact">Tel: ${reportCompany?.phone || ''}${reportCompany?.email ? `, Email: ${reportCompany.email}` : ''}</div>
          </div>
          <div class="report-title">${reportTitle}</div>
          <div class="report-subtitle">Generated on ${printDateTime}</div>
          <div class="report-date">Date: ${printDate}</div>
          ${bodyHtml}
          <div class="verified">Verified by: ____________________</div>
        </div>
      </body>
      </html>
    `);
    reportWindow.document.close();
    reportWindow.focus();
    reportWindow.print();
    reportWindow.close();
  };

  const formatMoney = (value, withDecimals = false, currency) => {
    const code = String(currency || "GMD").toUpperCase() === "USD" ? "USD" : "GMD";
    const symbol = code === "USD" ? "$" : "D";
    return `${symbol}${Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: withDecimals ? 2 : 0,
      maximumFractionDigits: withDecimals ? 2 : 0
    })}`;
  };

  const selectedMonthLabel = MONTHS[(Number(selectedMonth) || 1) - 1] || "";

  return (
    <div className="report-page">
      <div className="print-hide">
        <PageHeader title={user?.role === "SuperAdmin" ? "SuperAdmin Reports" : "Reports"} />
      </div>

      {/* General Reports Section */}
      <div className="print-hide bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-6 text-center">
          {user?.role === "SuperAdmin" ? "Select a platform report type to generate" : "Select a report type to generate"}
        </h2>

        <div className="max-w-md mx-auto space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Report Type: <span className="text-red-500">*</span>
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="">Select Report Type</option>
              <option value="Daily Report">Daily Report</option>
              <option value="Monthly Report">Monthly Report</option>
              <option value="Owner Report">Owner Report</option>
              <option value="Asset Report">Asset Report</option>
              <option value="Debtors Report">Debtors Report</option>
            </select>
          </div>

          {reportType === "Owner Report" && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Owner: <span className="text-red-500">*</span>
              </label>
              <select
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="">Select Owner</option>
                {owners.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Daily Report: Date Picker */}
          {reportType === "Daily Report" && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Date for Daily Report: <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dailyDate}
                onChange={(e) => setDailyDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          )}

          {(reportType === "Monthly Report" || reportType === "Owner Report") && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Month: <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  {MONTHS.map((m, index) => (
                    <option key={m} value={index + 1}>{m}</option>
                  ))}
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  {YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              {selectedMonth && selectedYear && (
                <p className="text-blue-600 font-semibold text-sm mt-2 text-center">
                  Selected: {selectedMonthLabel} {selectedYear}
                </p>
              )}
            </div>
          )}

          <button
            onClick={handleGenerateReport}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400"
          >
            <Assessment />
            {loading ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </div>

      {reportData && (
        <div className="report-print-area bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-8">
          <div className="flex justify-between gap-4 items-start mb-6 pb-4 border-b border-gray-200 print:pb-2">
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-xl border border-gray-200 bg-white flex items-center justify-center overflow-hidden shadow-sm">
                {companyLogoUrl ? (
                  <img
                    src={companyLogoUrl}
                    alt="Company logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-2xl font-bold text-teal-700">
                    {(reportCompany?.companyName || "C").charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 uppercase leading-tight">
                  {reportCompany?.companyName || "Company Name"}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {reportCompany?.address || "Address"}
                </p>
                <p className="text-sm text-gray-600">
                  Tel: {reportCompany?.phone || "Phone"}
                </p>
                <p className="text-sm text-gray-600">
                  Email: {reportCompany?.email || "Email"}
                </p>
              </div>
            </div>

            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors print:hidden"
            >
              <Print />
              Print Report
            </button>
          </div>

          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">{reportData.type}</h3>
            <p className="text-sm text-gray-500 mt-1">Generated on {new Date(reportData.generatedAt).toLocaleString()}</p>
          </div>

          {reportError && (
            <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <strong>Report API error:</strong> {reportError}
            </div>
          )}

          {reportData.type === "Daily Report" && (
            <>
              <div className="grid gap-3 sm:grid-cols-3 mb-6">
                <div className="border border-gray-200 p-4 text-sm font-semibold">Total Income<span className="mt-2 block text-lg font-bold">{formatMoney(reportData.totalAmount)}</span></div>
                <div className="border border-gray-200 p-4 text-sm font-semibold">Total Expenses<span className="mt-2 block text-lg font-bold">{formatMoney(reportData.totalExpenses)}</span></div>
                <div className="border border-gray-200 p-4 text-sm font-semibold">Net Balance<span className="mt-2 block text-lg font-bold">{formatMoney(reportData.netBalance)}</span></div>
              </div>

              <div className="overflow-x-auto border border-gray-200 mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Tenant</th>
                      <th className="px-3 py-2 text-left">Asset</th>
                      <th className="px-3 py-2 text-left">Location</th>
                      <th className="px-3 py-2 text-left">Amount</th>
                      <th className="px-3 py-2 text-left">Duration</th>
                      <th className="px-3 py-2 text-left">Paid For</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.paymentItems || []).length > 0 ? (
                      reportData.paymentItems.map((item, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="px-3 py-2">{item.tenantName || "-"}</td>
                          <td className="px-3 py-2">{item.assetName || "-"}</td>
                          <td className="px-3 py-2">{item.location || "-"}</td>
                          <td className="px-3 py-2">{formatMoney(item.amount, false, item.currency)}</td>
                          <td className="px-3 py-2">{item.billingPeriod || "-"}</td>
                          <td className="px-3 py-2">{item.paidFor || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-t border-gray-200">
                        <td className="px-3 py-2 text-center text-gray-500" colSpan={6}>No income entries</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {reportData.type === "Monthly Report" && (
            <>
              <div className="grid gap-3 sm:grid-cols-3 mb-6">
                <div className="border border-gray-200 p-4 text-sm font-semibold">Total Transactions<span className="mt-2 block text-lg font-bold">{reportData.totalTransactions ?? 0}</span></div>
                <div className="border border-gray-200 p-4 text-sm font-semibold">Total Income<span className="mt-2 block text-lg font-bold">{formatMoney(reportData.totalAmount)}</span></div>
                <div className="border border-gray-200 p-4 text-sm font-semibold">Period<span className="mt-2 block text-lg font-bold">{selectedMonthLabel} {selectedYear}</span></div>
              </div>

              <div className="overflow-x-auto border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-left">Tenant</th>
                      <th className="px-3 py-2 text-left">Amount (D)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.items || []).length > 0 ? (
                      reportData.items.map((item, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="px-3 py-2">{item.paymentDate || "-"}</td>
                          <td className="px-3 py-2">{item.assetName || "-"}{item.location ? ` - ${item.location}` : ""}{item.billingPeriod ? ` (${item.billingPeriod})` : ""}</td>
                          <td className="px-3 py-2">{item.tenantName || "-"}</td>
                          <td className="px-3 py-2">{formatMoney(item.amount, false, item.currency)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-t border-gray-200">
                        <td className="px-3 py-2 text-center text-gray-500" colSpan={4}>No monthly transactions</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {reportData.type === "Asset Report" && (
            <>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Asset Report</h3>
                <p className="text-sm text-gray-500 mt-1">Date: {new Date(reportData.generatedAt).toLocaleDateString()}</p>
              </div>

              <div className="overflow-x-auto border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Asset Name</th>
                      <th className="px-3 py-2 text-left">Category</th>
                      <th className="px-3 py-2 text-left">Location</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-left">Number of Units</th>
                      <th className="px-3 py-2 text-left">Owner Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.items || []).length > 0 ? (
                      reportData.items.map((item, index) => (
                        <tr key={item.id || index} className="border-t border-gray-200">
                          <td className="px-3 py-2">{item.assetName || "-"}</td>
                          <td className="px-3 py-2">{item.category || "-"}</td>
                          <td className="px-3 py-2">{item.location || "-"}</td>
                          <td className="px-3 py-2">{item.description || "-"}</td>
                          <td className="px-3 py-2">{item.rentalSpace ?? 0}</td>
                          <td className="px-3 py-2">{item.ownerName || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-t border-gray-200">
                        <td className="px-3 py-2 text-center text-gray-500" colSpan={6}>No assets found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {reportData.type === "Debtors Report" && (
            <>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Debtors Report</h3>
                <p className="text-sm text-gray-500 mt-1">Date: {new Date(reportData.generatedAt).toLocaleDateString()}</p>
              </div>

              <div className="overflow-x-auto border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Tenant Name</th>
                      <th className="px-3 py-2 text-left">Contact Number</th>
                      <th className="px-3 py-2 text-left">Asset</th>
                      <th className="px-3 py-2 text-left">Unit Number</th>
                      <th className="px-3 py-2 text-left">Contract Period</th>
                      <th className="px-3 py-2 text-left">Amount Paid</th>
                      <th className="px-3 py-2 text-left">Pending Balance</th>
                      <th className="px-3 py-2 text-left">Payment Method</th>
                      <th className="px-3 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.items || []).length > 0 ? (
                      reportData.items.map((item, index) => (
                        <tr key={item.id || index} className="border-t border-gray-200">
                          <td className="px-3 py-2">{item.tenantName || "-"}</td>
                          <td className="px-3 py-2">{item.contactNumber || "-"}</td>
                          <td className="px-3 py-2">{item.assetName || "-"}</td>
                          <td className="px-3 py-2">{item.unitNumber || "-"}</td>
                          <td className="px-3 py-2">{item.contractPeriod || [item.contractStart, item.contractEnd].filter(Boolean).join(" - ") || "-"}</td>
                          <td className="px-3 py-2">{formatMoney(item.totalPaid, true)}</td>
                          <td className="px-3 py-2">{formatMoney(item.pendingBalance, true)}</td>
                          <td className="px-3 py-2">{item.paymentMethod || "Cash"}</td>
                          <td className="px-3 py-2">{item.paymentStatus || "Pending"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-t border-gray-200">
                        <td className="px-3 py-2 text-center text-gray-500" colSpan={9}>No debtors found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {reportData.type === "Owner Report" && (
            <>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Owner Report - {reportData.ownerName || "-"}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {MONTHS[(Number(reportData.month) || selectedMonth) - 1] || selectedMonthLabel} {reportData.year || selectedYear}
                </p>
              </div>

              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-800 mb-3">Income - Rent Payments</h3>
              <div className="overflow-x-auto border border-gray-200 mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Tenant Name</th>
                      <th className="px-3 py-2 text-left">Asset Name</th>
                      <th className="px-3 py-2 text-left">Location</th>
                      <th className="px-3 py-2 text-left">Payment Date</th>
                      <th className="px-3 py-2 text-left">Contract Period</th>
                      <th className="px-3 py-2 text-left">Duration</th>
                      <th className="px-3 py-2 text-left">Amount Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.paymentItems || []).length > 0 ? (
                      reportData.paymentItems.map((item, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="px-3 py-2">{item.tenantName || "-"}</td>
                          <td className="px-3 py-2">{item.assetName || "-"}</td>
                          <td className="px-3 py-2">{item.location || "-"}</td>
                          <td className="px-3 py-2">{item.paymentDate || "-"}</td>
                          <td className="px-3 py-2">{item.contractPeriod || "-"}</td>
                          <td className="px-3 py-2">{item.duration || item.contractPeriod || "-"}</td>
                          <td className="px-3 py-2">{formatMoney(item.amount, false, item.currency)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-t border-gray-200">
                        <td className="px-3 py-2 text-center text-gray-500" colSpan={7}>No payment data found for the selected owner and month.</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="px-3 py-2 text-right font-semibold" colSpan={6}>Total Income:</td>
                      <td className="px-3 py-2 font-semibold">{formatMoney(reportData.totalRevenue ?? reportData.totalCollected)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-800 mb-3">Expenses - Maintenance &amp; Repairs</h3>
              <div className="overflow-x-auto border border-gray-200 mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Asset Name</th>
                      <th className="px-3 py-2 text-left">Location</th>
                      <th className="px-3 py-2 text-left">Maintenance Type</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Cost (D)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.maintenanceItems || []).length > 0 ? (
                      reportData.maintenanceItems.map((item, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="px-3 py-2">{item.assetName || "-"}</td>
                          <td className="px-3 py-2">{item.location || "-"}</td>
                          <td className="px-3 py-2">{item.maintenanceType || "-"}</td>
                          <td className="px-3 py-2">{item.description || "-"}</td>
                          <td className="px-3 py-2">{item.date || "-"}</td>
                          <td className="px-3 py-2">{formatMoney(item.cost)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-t border-gray-200">
                        <td className="px-3 py-2 text-center text-gray-500" colSpan={6}>No maintenance expenses found for the selected owner and month.</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="px-3 py-2 text-right font-semibold" colSpan={5}>Total Expenses:</td>
                      <td className="px-3 py-2 font-semibold">{formatMoney(reportData.totalMaintenanceExpenses)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <h3 className="text-center text-sm font-bold uppercase tracking-wide text-gray-800 mb-3">Financial Summary</h3>
              <div className="overflow-x-auto border border-gray-200">
                <table className="w-full text-sm">
                  <tbody>
                    <tr><td className="px-3 py-2 font-semibold">Total Revenue:</td><td className="px-3 py-2 text-right">{formatMoney(reportData.totalRevenue ?? reportData.totalCollected)}</td></tr>
                    <tr><td className="px-3 py-2 font-semibold">Management Commission (10%):</td><td className="px-3 py-2 text-right">{formatMoney(reportData.managementCommission)}</td></tr>
                    <tr><td className="px-3 py-2 font-semibold">Maintenance Expenses:</td><td className="px-3 py-2 text-right">{formatMoney(reportData.totalMaintenanceExpenses)}</td></tr>
                    <tr><td className="px-3 py-2 font-semibold">Net Amount To Owner:</td><td className="px-3 py-2 text-right">{formatMoney(reportData.netAmountToOwner)}</td></tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Report;